import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PanelLoading } from "~/components/loading";
import { ShareButton } from "~/components/share-button";
import type { ViewState } from "~/components/timeline";
import { Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import type { CompoundSearchCondition, CompoundVersionOption, CompoundView } from "~/features/compoundEntities/types";
import { CompoundEntitiesSearchControls } from "./CompoundEntitiesSearchControls";
import { CompoundEntitiesTable } from "./CompoundEntitiesTable";
import { CompoundEntitiesTimelineView } from "./CompoundEntitiesTimelineView";
import { CompoundEntitiesViewSwitcher } from "./CompoundEntitiesViewSwitcher";
import { CompoundEntityPeek } from "./CompoundEntityPeek";
import { CompoundGdtfFilter } from "./CompoundGdtfFilter";

interface CompoundEntitiesPresenterProps {
  view: CompoundView;
  onViewChange: (next: CompoundView) => void;
  searchConditions: CompoundSearchCondition[];
  onConditionsChange: (next: CompoundSearchCondition[]) => void;
  onPageReset: () => void;
  gdtfFrom?: string;
  gdtfTo?: string;
  onGdtfCommit: (from: string | undefined, to: string | undefined) => void;
  versions: CompoundVersionOption[];
  selectedVersion?: string;
  onVersionChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
  selectedCompoundEntity: string | null;
  onCloseSelected: () => void;
  initialTimelineViewState: ViewState | undefined;
  onTimelineViewStateChange: (centerYear: number, scale: number) => void;
}

export function CompoundEntitiesPresenter({
  view,
  onViewChange,
  searchConditions,
  onConditionsChange,
  onPageReset,
  gdtfFrom,
  gdtfTo,
  onGdtfCommit,
  versions,
  selectedVersion,
  onVersionChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
  selectedCompoundEntity,
  onCloseSelected,
  initialTimelineViewState,
  onTimelineViewStateChange,
}: CompoundEntitiesPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-dewey-title capitalize text-fg-1">{t("model.compoundEntity.modelNames")}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {versions.length > 0 && (
            <div className="flex items-center gap-2">
              <Label htmlFor="compound-version" className="text-12-bold text-fg-2">
                {t("ui.version")}
              </Label>
              <Select value={selectedVersion} onValueChange={onVersionChange}>
                <SelectTrigger id="compound-version" className="min-w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((v) => (
                    <SelectItem key={v.id} value={v.version}>
                      {v.version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="button" variant="outline" size="sm" asChild>
            <Link to="/compound-record-versions">{t("model.compoundRecordVersion.modelNames")}</Link>
          </Button>
          <ShareButton />
          <CompoundEntitiesViewSwitcher value={view} onChange={onViewChange} />
        </div>
      </div>

      <div className="space-y-3">
        <CompoundEntitiesSearchControls
          conditions={searchConditions}
          onConditionsChange={onConditionsChange}
          onPageReset={onPageReset}
        />
        <CompoundGdtfFilter gdtfFrom={gdtfFrom} gdtfTo={gdtfTo} onCommit={onGdtfCommit} />
      </div>

      {view === "table" && (
        <Suspense fallback={<PanelLoading />}>
          <CompoundEntitiesTable
            conditions={searchConditions}
            version={selectedVersion}
            gdtfFrom={gdtfFrom}
            gdtfTo={gdtfTo}
            pageIndex={pageIndex}
            onPageIndexChange={onPageIndexChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            isPending={isPending}
            onSelect={onSelect}
          />
        </Suspense>
      )}
      {view === "timeline" && (
        <Suspense fallback={<PanelLoading />}>
          <CompoundEntitiesTimelineView
            conditions={searchConditions}
            version={selectedVersion}
            gdtfFrom={gdtfFrom}
            gdtfTo={gdtfTo}
            onSelect={onSelect}
            initialViewState={initialTimelineViewState}
            onViewStateChange={onTimelineViewStateChange}
          />
        </Suspense>
      )}

      <CompoundEntityPeek id={selectedCompoundEntity} onClose={onCloseSelected} />
    </div>
  );
}
